import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { useEmployeeContext } from '@/shared/contexts/EmployeeContext';
import { useDescribeState } from '../../describe-stage/context/DescribeStateContext';
import { useDebriefFactsState } from '../../shared/context/DebriefFactsStateContext';
import { useDeepDiveState } from '../../deep-dive-stage/context/DeepDiveStateContext';
import type {
  MatchPointSession,
  Turn,
} from '../../deep-dive-stage/types/deepDive.types';
import type { EditableFact } from '../../shared/types';
import { LessonsHeader } from './LessonsHeader';
import { MatchPointTabs } from './MatchPointTabs';
import { LessonsMatrix } from './LessonsMatrix';

function clampTabIndex(rawIndex: number, count: number): number {
  if (count === 0) return 0;
  return Math.min(Math.max(rawIndex, 0), count - 1);
}

interface LessonsPageProps {
  sessionId: string;
}

export function LessonsPage({ sessionId }: LessonsPageProps) {
  const { t } = useTranslation('debriefV2');
  const navigate = useNavigate();
  const leader = useEmployeeContext();
  const { state: describeState } = useDescribeState();
  const {
    facts,
    factsByStage,
    isLoading: isFactsLoading,
  } = useDebriefFactsState();
  const { state: deepDiveState, dispatch } = useDeepDiveState();

  const completedSessionsWithIndex = useMemo(
    () =>
      deepDiveState.matchPoints
        .map((session, index) => ({ session, index }))
        .filter(({ session }) => session.isCompleted),
    [deepDiveState.matchPoints]
  );

  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const clampedTabIndex = clampTabIndex(
    activeTabIndex,
    completedSessionsWithIndex.length
  );

  useEffect(() => {
    if (!describeState.isComplete) {
      navigate({
        to: '/interactions/debrief-v2/$id/describe',
        params: { id: sessionId },
        replace: true,
      });
      return;
    }
    if (!isFactsLoading && facts.length === 0) {
      navigate({
        to: '/interactions/debrief-v2/$id/gather',
        params: { id: sessionId },
        replace: true,
      });
      return;
    }
    if (!isFactsLoading && deepDiveState.matchPoints.length === 0) {
      navigate({
        to: '/interactions/debrief-v2/$id/match-points',
        params: { id: sessionId },
        replace: true,
      });
      return;
    }
    if (
      !isFactsLoading &&
      deepDiveState.matchPoints.every((session) => !session.isCompleted)
    ) {
      navigate({
        to: '/interactions/debrief-v2/$id/deep-dive',
        params: { id: sessionId },
        replace: true,
      });
    }
  }, [
    describeState.isComplete,
    isFactsLoading,
    facts.length,
    deepDiveState.matchPoints,
    navigate,
    sessionId,
  ]);

  const goalAnswer = describeState.goal;
  const outcomeAnswer = describeState.outcome;
  const dateAnswer = describeState.date;
  const peopleAnswer = describeState.people;

  if (
    !leader ||
    !describeState.isComplete ||
    !goalAnswer ||
    !outcomeAnswer ||
    !dateAnswer ||
    !peopleAnswer ||
    isFactsLoading ||
    deepDiveState.matchPoints.length === 0 ||
    completedSessionsWithIndex.length === 0
  ) {
    return <main className="flex-1 w-full" />;
  }

  const activeEntry = completedSessionsWithIndex[clampedTabIndex];
  if (!activeEntry) {
    return <main className="flex-1 w-full" />;
  }
  const activeSession = activeEntry.session;
  const activeMatchPointIndex = activeEntry.index;
  const fact = findActiveFact(activeSession, factsByStage);

  const participants = peopleAnswer.employees;
  const headerParticipants = [leader, ...participants];

  const conductedOn = new Date(dateAnswer.startDate);
  const outcome = outcomeAnswer.result;
  const goalDescription = goalAnswer.description;

  return (
    <main className="flex-1 flex flex-col bg-slate-50/50 overflow-hidden">
      <LessonsHeader
        goalDescription={goalDescription}
        conductedOn={conductedOn}
        outcome={outcome}
        participants={headerParticipants}
      />
      <div className="flex-1 flex flex-col overflow-hidden relative">
        <MatchPointTabs
          matchPointStages={completedSessionsWithIndex.map(
            ({ session }) => session.stage
          )}
          activeIndex={clampedTabIndex}
          onSelect={setActiveTabIndex}
        />
        <LessonsMatrix
          leader={leader}
          participants={participants}
          session={activeSession}
          fact={fact}
          onEditLesson={(lessonIndex, lesson) =>
            dispatch({
              type: 'EDIT_LESSON',
              payload: {
                matchPointIndex: activeMatchPointIndex,
                lessonIndex,
                lesson,
              },
            })
          }
          onDeleteLesson={(lessonIndex) =>
            dispatch({
              type: 'DELETE_LESSON',
              payload: { matchPointIndex: activeMatchPointIndex, lessonIndex },
            })
          }
          onAddLesson={(lesson) =>
            dispatch({
              type: 'ADD_LESSON',
              payload: { matchPointIndex: activeMatchPointIndex, lesson },
            })
          }
        />
        <footer className="w-full h-[72px] bg-white border-t border-slate-200 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.03)] shrink-0 flex items-center justify-end px-8">
          <button
            type="button"
            disabled
            title="Coming soon"
            className="h-10 px-6 bg-slate-300 text-slate-500 rounded-md text-sm font-medium flex items-center gap-2 cursor-not-allowed"
          >
            {t('lessons.finishDebrief')}
            <Check className="h-4 w-4" aria-hidden />
          </button>
        </footer>
      </div>
    </main>
  );
}

function findActiveFact(
  session: MatchPointSession,
  factsByStage: ReturnType<typeof useDebriefFactsState>['factsByStage']
): EditableFact | null {
  const factPickTurn = session.turns.find(
    (turn): turn is Extract<Turn, { kind: 'fact-pick' }> =>
      turn.kind === 'fact-pick'
  );
  if (!factPickTurn || !factPickTurn.factId) return null;
  const stageFacts = factsByStage[session.stage] ?? [];
  return stageFacts.find((fact) => fact.id === factPickTurn.factId) ?? null;
}
